import React from 'react';
import {
  Text, View, Dimensions, StyleSheet,
  ScrollView,
} from 'react-native';
import { useQuery } from '@apollo/client';
import { PieChart } from 'react-native-svg-charts';
import { GetStatistics } from '../../gql/statistics/query';
import { theme } from '../Common.style';
import LoadingIndicator from '../../components/commons/LoadingIndicator';

const PACK_LABELS = {
  everyday: 'Objets du quotidien',
  sports: 'Sports & Arts martiaux',
  food: 'Nourriture & Boissons',
  animals: 'Animaux',
  places: 'Pays & Lieux',
};

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    color: theme.colors.error,
    fontSize: 16,
    textAlign: 'center',
  },
  header: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 20,
    color: theme.colors.text,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: theme.colors.text,
  },
  legendContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 10,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
    marginBottom: 5,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 5,
  },
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  statBlock: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 36,
    fontWeight: '900',
    color: theme.colors.primary,
  },
  statLabel: {
    fontSize: 12,
    color: '#888',
    marginTop: 2,
    textAlign: 'center',
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: '#eee',
  },
  leaderboardTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#555',
    marginBottom: 8,
  },
  leaderboardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  leaderboardRank: {
    width: 28,
    fontSize: 13,
    fontWeight: '700',
    color: '#aaa',
  },
  leaderboardName: {
    flex: 1,
    fontSize: 15,
    color: '#333',
    fontWeight: '600',
  },
  leaderboardGames: {
    fontSize: 13,
    color: '#888',
    marginRight: 10,
  },
  leaderboardScore: {
    fontSize: 14,
    fontWeight: '800',
    color: theme.colors.primary,
    minWidth: 48,
    textAlign: 'right',
  },
  packRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    gap: 8,
  },
  packName: {
    width: 150,
    fontSize: 13,
    color: '#444',
  },
  packBarTrack: {
    flex: 1,
    height: 8,
    backgroundColor: '#eee',
    borderRadius: 4,
    overflow: 'hidden',
  },
  packBarFill: {
    height: '100%',
    backgroundColor: theme.colors.primary,
    borderRadius: 4,
  },
  packCount: {
    width: 64,
    fontSize: 12,
    color: '#888',
    textAlign: 'right',
  },
});

export default function StatisticsScreen() {
  const { data, loading, error } = useQuery(GetStatistics);

  if (loading) {
    return (
      <LoadingIndicator text="Chargement des statistiques..." />
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>
          Error:
          {error.message}
        </Text>
      </View>
    );
  }

  const notificationStats = data?.notificationStats;
  const userStatsByStatus = data?.userStatsByStatus;
  const userStatsByPlatform = data?.userStatsByPlatform;
  const gameStats = data?.gameStats;

  const pieDataStatus = userStatsByStatus?.map((item, index) => ({
    key: `status-${index}`,
    value: item.count,
    svg: { fill: `rgba(134, 65, 244, ${(index + 1) / userStatsByStatus.length})` },
    arc: { outerRadius: '100%', padAngle: 0.05 },
    label: item.status,
  }));

  const pieDataNotificationToken = userStatsByPlatform?.map((item, index) => ({
    key: `token-${index}`,
    value: item.count,
    svg: { fill: `rgba(200, 100, 50, ${(index + 1) / userStatsByPlatform.length})` },
    arc: { outerRadius: '100%', padAngle: 0.05 },
    label: item.type,
  }));

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.header}>Statistiques</Text>

      {notificationStats && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Notification</Text>
          <Text>
            Sent:
            {notificationStats.sent}
          </Text>
          <Text>
            Read:
            {notificationStats.read}
          </Text>
        </View>
      )}

      {userStatsByStatus && userStatsByStatus.length > 0 && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Utilisateurs par Status</Text>
          <PieChart
            style={{ height: 200, width: width - 40 }}
            data={pieDataStatus}
            outerRadius="70%"
            innerRadius={20}
            labelRadius={90}
            padAngle={0}
          />
          <View style={styles.legendContainer}>
            {userStatsByStatus.map((item, index) => (
              <View key={`legend-status-${index}`} style={styles.legendItem}>
                <View style={[styles.legendColor, { backgroundColor: `rgba(134, 65, 244, ${(index + 1) / userStatsByStatus.length})` }]} />
                <Text>
                  {item.status}
                  {' '}
                  (
                  {item.count}
                  )
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {userStatsByPlatform && userStatsByPlatform.length > 0 && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Utilisateurs par plateforme</Text>
          <PieChart
            style={{ height: 200, width: width - 40 }}
            data={pieDataNotificationToken}
            outerRadius="70%"
            innerRadius={20}
            labelRadius={90}
            padAngle={0}
          />
          <View style={styles.legendContainer}>
            {userStatsByPlatform.map((item, index) => (
              <View key={`legend-token-${index}`} style={styles.legendItem}>
                <View style={[styles.legendColor, { backgroundColor: `rgba(200, 100, 50, ${(index + 1) / userStatsByPlatform.length})` }]} />
                <Text>
                  {item.type}
                  {' '}
                  (
                  {item.count}
                  )
                </Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {gameStats && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Jeu de l&apos;Imposteur</Text>
          <View style={styles.statRow}>
            <View style={styles.statBlock}>
              <Text style={styles.statValue}>{gameStats.totalGames}</Text>
              <Text style={styles.statLabel}>Parties jouées</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statBlock}>
              <Text style={styles.statValue}>{gameStats.totalRounds}</Text>
              <Text style={styles.statLabel}>Manches terminées</Text>
            </View>
          </View>
          <View style={styles.statRow}>
            <View style={styles.statBlock}>
              <Text style={styles.statValue}>{gameStats.avgPlayersPerGame}</Text>
              <Text style={styles.statLabel}>Joueurs moy. / partie</Text>
            </View>
            <View style={styles.statDivider} />
            <View style={styles.statBlock}>
              <Text style={styles.statValue}>{gameStats.maxPlayersPerGame}</Text>
              <Text style={styles.statLabel}>Max joueurs / partie</Text>
            </View>
          </View>

          {gameStats.packStats.length > 0 && (
            <>
              <Text style={styles.leaderboardTitle}>Packs utilisés</Text>
              {gameStats.packStats.map((p) => {
                const total = gameStats.packStats.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
                return (
                  <View key={p.pack} style={styles.packRow}>
                    <Text style={styles.packName}>{PACK_LABELS[p.pack] ?? p.pack}</Text>
                    <View style={styles.packBarTrack}>
                      <View style={[styles.packBarFill, { width: `${pct}%` }]} />
                    </View>
                    <Text style={styles.packCount}>
                      {p.count}
                      {` (${pct}%)`}
                    </Text>
                  </View>
                );
              })}
            </>
          )}

          {gameStats.topPlayers.length > 0 && (
            <>
              <Text style={styles.leaderboardTitle}>Joueurs les plus actifs</Text>
              {gameStats.topPlayers.map((p) => (
                <View key={`${p.firstName}-${p.lastName}`} style={styles.leaderboardRow}>
                  <Text style={styles.leaderboardRank}>
                    #
                    {gameStats.topPlayers.indexOf(p) + 1}
                  </Text>
                  <Text style={styles.leaderboardName}>
                    {p.firstName}
                    {' '}
                    {p.lastName}
                  </Text>
                  <Text style={styles.leaderboardGames}>
                    {p.gamesPlayed}
                    {' parties'}
                  </Text>
                  <Text style={styles.leaderboardScore}>
                    {p.totalScore}
                    {' pts'}
                  </Text>
                </View>
              ))}
            </>
          )}
        </View>
      )}
    </ScrollView>
  );
}
